// =============================================
// COMPONENTE: WhatsAppAdvancedNode - Nó Avançado WhatsApp
// =============================================
// Arquivo: WhatsAppAdvancedNode.tsx
// Descrição: Nó completo com todas as operações WhatsApp disponíveis
// =============================================

import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { MessageSquare, Users, Settings, Zap, FileText } from 'lucide-react';
import ActionsBadge from './ActionsBadge';
import '../NodeStyles.css';

const WhatsAppAdvancedNode: React.FC<NodeProps> = ({ data, selected }) => {
  const {
    label,
    operation,
    category,
    phoneNumber,
    description,
    actions
  } = data;

  const getCategoryIcon = () => {
    switch (category) {
      case 'messages':
        return <Zap size={18} />;
      case 'groups':
        return <Users size={18} />;
      case 'profile':
        return <Settings size={18} />;
      case 'media':
        return <FileText size={18} />;
      default:
        return <MessageSquare size={18} />;
    }
  };

  const getOperationLabel = (operation: string) => {
    const operations: Record<string, string> = {
      // Mensagens
      'send_text': '📝 Enviar Texto',
      'send_media': '🖼️ Enviar Mídia',
      'send_audio': '🎵 Enviar Áudio',
      'send_sticker': '😀 Enviar Sticker',
      'send_location': '📍 Enviar Localização',
      'send_contact': '👤 Enviar Contato',
      'send_reaction': '❤️ Enviar Reação',
      'send_poll': '📊 Enviar Enquete',
      'send_list': '📋 Enviar Lista',
      'send_buttons': '🔘 Enviar Botões',

      // Chats
      'mark_read': '✓ Marcar como Lido',
      'mark_unread': '• Marcar como Não Lido',
      'archive_chat': '📦 Arquivar Chat',
      'delete_message': '🗑️ Deletar Mensagem',

      // Grupos
      'create_group': '➕ Criar Grupo',
      'get_group_info': 'ℹ️ Info do Grupo',
      'update_group': '✏️ Atualizar Grupo',
      'add_participants': '👥 Adicionar Membros',
      'remove_participants': '❌ Remover Membros',
      'promote_admin': '⭐ Promover Admin',
      'demote_admin': '⬇️ Remover Admin',
      'leave_group': '👋 Sair do Grupo',
      'get_invite_code': '🔗 Obter Link de Convite',

      // Perfil
      'fetch_profile': '👤 Buscar Perfil',
      'update_profile_name': '✏️ Atualizar Nome',
      'update_profile_status': '💬 Atualizar Status',
      'update_profile_picture': '🖼️ Atualizar Foto',

      // Configurações
      'set_presence': '🟢 Definir Presença',
      'set_webhook': '🔔 Configurar Webhook',
      'update_privacy': '🔒 Atualizar Privacidade',

      // Avançado
      'forward_message': '↪️ Encaminhar Mensagem',
      'download_media': '⬇️ Baixar Mídia',
      'star_message': '⭐ Favoritar Mensagem',
      'backup_session': '💾 Backup de Sessão',
      'restore_session': '♻️ Restaurar Sessão'
    };

    return operations[operation] || operation;
  };

  return (
    <div className={`flow-node whatsapp-advanced-node ${selected ? 'selected' : ''}`}>
      <Handle
        type="target"
        position={Position.Top}
        className="flow-handle"
      />

      <div className="node-header whatsapp-advanced-header">
        {getCategoryIcon()}
        <span className="node-title">{label || 'WhatsApp'}</span>
      </div>

      <div className="node-body">
        {operation && (
          <div className="node-detail">
            <span className="node-detail-text">
              {getOperationLabel(operation)}
            </span>
          </div>
        )}
        {phoneNumber && (
          <div className="node-badge">
            📱 {phoneNumber}
          </div>
        )}
        {description && (
          <div className="node-description">{description}</div>
        )}
        <ActionsBadge actions={actions} />
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        className="flow-handle"
      />
    </div>
  );
};

export default WhatsAppAdvancedNode;
